/**
 * ActivityChart — Inline SVG bar chart showing daily work activity.
 *
 * Modes:
 *   'single'  — one color, daily hours for a single project
 *   'stacked' — multi-color stacked bars, hours per project per day
 *   'sparkline' — tiny inline chart, no labels/tooltips
 *
 * Props:
 *   entries      — [{ date: 'YYYY-MM-DD', hours: number, jobId?: string, jobTitle?: string }]
 *   mode         — 'single' | 'stacked' | 'sparkline'
 *   projectColors — Map<jobId, color> (for stacked mode)
 *   color        — bar color for single mode (default teal)
 *   height       — chart height in px (default 120, sparkline 24)
 *   days         — how many days to display (default 14)
 */

import { useState, useMemo } from 'react';

const DEFAULT_COLOR = '#5DCAA5';
const EMPTY_COLOR = '#e5e7eb';

function generateDateRange(days) {
  const dates = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    dates.push(`${y}-${m}-${day}`);
  }
  return dates;
}

function formatDateLabel(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatWeekday(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short' });
}

export default function ActivityChart({
  entries = [],
  mode = 'single',
  projectColors = new Map(),
  color = DEFAULT_COLOR,
  height = 120,
  days = 14,
  showEarnings = false,
}) {
  const [tooltip, setTooltip] = useState(null); // { mouseX, mouseY, date, total, breakdown }
  const [containerRef, setContainerRef] = useState(null);

  const dateRange = useMemo(() => generateDateRange(days), [days]);

  // Group entries by date → { date: [{ hours, jobId, jobTitle }] }
  const byDate = useMemo(() => {
    const map = {};
    for (const date of dateRange) map[date] = [];
    for (const entry of entries) {
      const date = (entry.date || '').slice(0, 10);
      if (map[date]) {
        map[date].push({
          hours: parseFloat(entry.hours) || 0,
          jobId: entry.jobId || entry.job_id || null,
          jobTitle: entry.jobTitle || entry.job?.title || null,
          earned: parseFloat(entry.earned) || 0,
        });
      }
    }
    return map;
  }, [entries, dateRange]);

  // Compute max hours in any single day (for Y-axis scaling)
  const maxHours = useMemo(() => {
    let max = 0;
    for (const date of dateRange) {
      const dayTotal = (byDate[date] || []).reduce((s, e) => s + e.hours, 0);
      if (dayTotal > max) max = dayTotal;
    }
    return Math.max(max, 1); // prevent divide by zero
  }, [byDate, dateRange]);

  // For stacked mode: get consistent project ordering
  const projectOrder = useMemo(() => {
    if (mode !== 'stacked') return [];
    const ids = new Set();
    for (const entry of entries) {
      const jid = entry.jobId || entry.job_id;
      if (jid) ids.add(jid);
    }
    return [...ids].sort();
  }, [entries, mode]);

  const isSparkline = mode === 'sparkline';
  const showLabels = !isSparkline && days <= 14;
  const chartWidth = 300;
  const padding = { top: 4, bottom: 4, left: 0, right: 0 };
  const chartAreaWidth = chartWidth - padding.left - padding.right;
  const chartAreaHeight = height - padding.top - padding.bottom;
  const barGap = isSparkline ? 1 : 2;
  const barWidth = Math.max(2, (chartAreaWidth - barGap * dateRange.length) / dateRange.length);

  if (entries.length === 0 && !isSparkline) {
    return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#9ca3af' }}>No activity data</div>;
  }

  return (
    <div ref={setContainerRef} style={{ position: 'relative', width: '100%' }}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${chartWidth} ${height}`}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        {dateRange.map((date, i) => {
          const dayEntries = byDate[date] || [];
          const dayTotal = dayEntries.reduce((s, e) => s + e.hours, 0);
          const x = padding.left + i * (barWidth + barGap);

          if (dayTotal === 0) {
            // Empty day — thin grey line
            const lineHeight = Math.max(2, chartAreaHeight * 0.05);
            return (
              <rect
                key={date}
                x={x}
                y={padding.top + chartAreaHeight - lineHeight}
                width={barWidth}
                height={lineHeight}
                fill={EMPTY_COLOR}
                rx={1}
              />
            );
          }

          if (mode === 'stacked' && projectOrder.length >= 1) {
            // Stacked bars — render segments bottom-up. Also used for 1-project case so tooltip shows project detail.
            let yOffset = 0;
            const segments = [];
            // Group entries by jobId for this day (aggregate hours + earnings)
            const byJob = {};
            for (const e of dayEntries) {
              const jid = e.jobId || 'unknown';
              if (!byJob[jid]) byJob[jid] = { hours: 0, earned: 0 };
              byJob[jid].hours += e.hours;
              byJob[jid].earned += e.earned || 0;
            }
            for (const jid of projectOrder) {
              const { hours: h } = byJob[jid] || { hours: 0 };
              if (h <= 0) continue;
              const barH = (h / maxHours) * chartAreaHeight;
              const barY = padding.top + chartAreaHeight - yOffset - barH;
              const barColor = projectColors.get(jid) || color;
              segments.push(
                <rect
                  key={`${date}-${jid}`}
                  x={x}
                  y={barY}
                  width={barWidth}
                  height={Math.max(1, barH)}
                  fill={barColor}
                  rx={1}
                />
              );
              yOffset += barH;
            }
            return (
              <g key={date}
                onMouseEnter={!isSparkline ? (e) => {
                  const cr = containerRef?.getBoundingClientRect();
                  setTooltip({
                    mouseX: e.clientX - (cr?.left || 0),
                    mouseY: e.clientY - (cr?.top || 0),
                    date,
                    total: dayTotal,
                    breakdown: Object.entries(byJob).map(([jid, v]) => ({
                      jobId: jid,
                      hours: v.hours,
                      earned: v.earned,
                      title: dayEntries.find(e => (e.jobId || 'unknown') === jid)?.jobTitle || jid.slice(0, 8),
                      color: projectColors.get(jid) || color,
                    })),
                  });
                } : undefined}
                onMouseMove={!isSparkline ? (e) => {
                  const cr = containerRef?.getBoundingClientRect();
                  setTooltip(prev => prev ? { ...prev, mouseX: e.clientX - (cr?.left || 0), mouseY: e.clientY - (cr?.top || 0) } : prev);
                } : undefined}
                onMouseLeave={!isSparkline ? () => setTooltip(null) : undefined}
              >
                {segments}
              </g>
            );
          }

          // Single bar
          const barH = (dayTotal / maxHours) * chartAreaHeight;
          return (
            <rect
              key={date}
              x={x}
              y={padding.top + chartAreaHeight - barH}
              width={barWidth}
              height={Math.max(1, barH)}
              fill={color}
              rx={1}
              onMouseEnter={!isSparkline ? (e) => {
                const cr = containerRef?.getBoundingClientRect();
                setTooltip({
                  mouseX: e.clientX - (cr?.left || 0),
                  mouseY: e.clientY - (cr?.top || 0),
                  date,
                  total: dayTotal,
                  breakdown: null,
                });
              } : undefined}
              onMouseMove={!isSparkline ? (e) => {
                const cr = containerRef?.getBoundingClientRect();
                setTooltip(prev => prev ? { ...prev, mouseX: e.clientX - (cr?.left || 0), mouseY: e.clientY - (cr?.top || 0) } : prev);
              } : undefined}
              onMouseLeave={!isSparkline ? () => setTooltip(null) : undefined}
              style={{ cursor: isSparkline ? 'default' : 'pointer' }}
            />
          );
        })}

        {/* X-axis labels rendered as HTML below the SVG to avoid text stretching */}
      </svg>

      {/* HTML date labels (not inside SVG to avoid stretching) */}
      {showLabels && (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0 0' }}>
          {dateRange.map((date, i) => {
            if (days > 7 && i % 2 !== 0 && i !== dateRange.length - 1) return <span key={date} style={{ flex: 1 }} />;
            return (
              <span key={date} style={{ flex: 1, textAlign: 'center', fontSize: 8, fontFamily: 'var(--font-mono)', color: '#9ca3af' }}>
                {days <= 7 ? formatWeekday(date) : formatDateLabel(date)}
              </span>
            );
          })}
        </div>
      )}

      {/* Tooltip */}
      {tooltip && !isSparkline && (
        <div style={{
          position: 'absolute',
          top: tooltip.mouseY - 8,
          left: tooltip.mouseX + 12,
          transform: 'translateY(-100%)',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '6px 10px',
          zIndex: 10,
          minWidth: 100,
          pointerEvents: 'none',
        }}>
          <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: '#9ca3af', marginBottom: 3 }}>{formatDateLabel(tooltip.date)}</div>
          {tooltip.breakdown && tooltip.breakdown.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {tooltip.breakdown.map(b => (
                <div key={b.jobId} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: b.color, flexShrink: 0 }} />
                  <span style={{ color: '#0a0a0a', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{b.title}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', color: '#6b7280' }}>{b.hours.toFixed(1)}h</span>
                  {showEarnings && b.earned > 0 && <span style={{ fontFamily: 'var(--font-mono)', color: '#5DCAA5', fontWeight: 600 }}>${Math.round(b.earned)}</span>}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-mono)', color: '#0a0a0a' }}>{tooltip.total.toFixed(1)}h</div>
          )}
        </div>
      )}
    </div>
  );
}
